/**
 * A Firestore listener, surfaced as a TanStack Query entry.
 *
 * `onSnapshot`, not `getDocs`, everywhere in this app. Three reasons, and the
 * third is the one that matters here:
 *
 *   - two phones in the same whelping room must agree without a pull-to-refresh;
 *   - the persistent cache answers a listener *immediately* from disk and then
 *     again from the server, which is what makes a cold start paint instantly;
 *   - a queued offline write is echoed back through the listener straight away
 *     (`hasPendingWrites`), so the weight appears the moment it is typed. A
 *     `getDocs` would answer from the cache once and never mention it.
 *
 * The query key is written once, in a `*QueryOptions` factory, and both the
 * route loader and the component name the same one. A loader that filled a key
 * the component does not read is a screen that stays empty forever — no error,
 * no warning, just nothing.
 */
import { useQueryClient } from '@tanstack/react-query';
import { type DocumentReference, onSnapshot, type Query } from 'firebase/firestore';
import { useEffect } from 'react';

import { SNAPSHOT_OPTIONS } from '@/api/converter.ts';

/**
 * Keep `queryKey` fed from `query` for as long as the component is mounted.
 *
 * The unsubscribe is not optional. An abandoned listener keeps receiving — and
 * being billed for — every change to its result set for the life of the tab.
 */
export const useLiveCollection = <T>(
  queryKey: readonly unknown[],
  query: Query<T>,
  /**
   * Whether to subscribe at all.
   *
   * For a query this reader is not allowed to run. A listener the rules refuse
   * fails once, terminally, and lands in the cache as a thrown error — so a
   * section that is simply not for this person would take the whole screen down
   * rather than be absent from it. See `canView` in platform/src/roles.ts and
   * the read rules in app/firestore.rules.
   *
   * It is a parameter and not "don't call the hook", because hooks cannot be
   * called conditionally and the caller that knows the answer is often the one
   * above the component that reads the data.
   */
  enabled = true,
): void => {
  const client = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const stop = onSnapshot(
      query,
      (snap) => {
        client.setQueryData(
          queryKey,
          snap.docs.map((d) => d.data(SNAPSHOT_OPTIONS)),
        );
      },
      (error) => {
        // A listener error is terminal — Firestore does not retry it — so it
        // must reach the UI rather than leaving a screen frozen on stale data.
        client.setQueryData(queryKey, () => {
          throw error;
        });
      },
    );
    return stop;
    // `query` is rebuilt on every render by its caller; the key is what
    // identifies it. This is the one place that trade is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, JSON.stringify(queryKey), enabled]);
};

/**
 * The same, for a single document.
 *
 * A document that does not exist sets `null` rather than leaving the key
 * pending: "there is no tenant with this id" is an answer, and a component that
 * waits for it forever renders a spinner that never resolves.
 */
export const useLiveDocument = <T>(
  queryKey: readonly unknown[],
  ref: DocumentReference<T>,
): void => {
  const client = useQueryClient();

  useEffect(() => {
    const stop = onSnapshot(
      ref,
      (snap) => {
        client.setQueryData(queryKey, snap.exists() ? snap.data(SNAPSHOT_OPTIONS) : null);
      },
      (error) => {
        client.setQueryData(queryKey, () => {
          throw error;
        });
      },
    );
    return stop;
    // As above: `ref` is rebuilt every render, the key is what identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, JSON.stringify(queryKey)]);
};
